import React, { useState, useEffect } from 'react';
import { Industry } from '../../../types';
import { apiService } from '../../../services';
import styles from './IndustrySelector.module.css';

interface IndustrySelectorProps {
  onIndustrySelect: (industry: Industry) => void;
  selectedIndustry?: Industry | null;
}

const IndustrySelector: React.FC<IndustrySelectorProps> = ({
  onIndustrySelect,
  selectedIndustry
}) => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadIndustries();
  }, []);

  const loadIndustries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getIndustries();
      setIndustries(data);
    } catch (err) {
      setError('Error al cargar las industrias. Por favor intenta de nuevo.');
      console.error('Error loading industries:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredIndustries = industries.filter(industry =>
    industry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    industry.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getIndustryIcon = (iconName?: string) => {
    const iconMap: { [key: string]: string } = {
      plane: 'fas fa-plane',
      wifi: 'fas fa-wifi',
      heart: 'fas fa-heart',
      scale: 'fas fa-balance-scale',
      cog: 'fas fa-cog'
    };
    return iconMap[iconName || ''] || 'fas fa-building';
  };

  const handleIndustryClick = (industry: Industry) => {
    onIndustrySelect(industry);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Cargando industrias...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={loadIndustries} className={styles.retryButton}>
          Intentar de Nuevo
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Selecciona tu Industria</h2>
        <p>Elige la industria que mejor describe tu organización</p>
      </div>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Buscar industrias..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.industriesGrid}>
        {filteredIndustries.map((industry) => (
          <div
            key={industry.id}
            className={`${styles.industryCard} ${
              selectedIndustry?.id === industry.id ? styles.selected : ''
            }`}
            onClick={() => handleIndustryClick(industry)}
            style={{
              '--theme-color': industry.themeColor || '#3B82F6'
            } as React.CSSProperties}
          >
            <div className={styles.industryIcon}>
              <i className={getIndustryIcon(industry.icon)}></i>
            </div>
            <div className={styles.industryContent}>
              <h3 className={styles.industryName}>{industry.name}</h3>
              <p className={styles.industryDescription}>{industry.description}</p>
            </div>
            {selectedIndustry?.id === industry.id && (
              <div className={styles.selectedIndicator}>
                <i className="fas fa-check"></i>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredIndustries.length === 0 && searchTerm && (
        <div className={styles.noResults}>
          <p>No se encontraron industrias que coincidan con "{searchTerm}"</p>
          <button
            onClick={() => setSearchTerm('')}
            className={styles.clearSearchButton}
          >
            Limpiar Búsqueda
          </button>
        </div>
      )}
    </div>
  );
};

export default IndustrySelector;
